/**
 * Pre-built Zod schemas for all tools
 * Eliminates runtime schema generation and improves compilation speed
 */

import { z } from 'zod';
import { createArtifactInputSchema } from '../../artifacts/create-artifact-tool.js';

/**
 * Query tool schemas
 */

export const findContactSchema = z.object({
  id: z.string().optional().describe('UUID of the contact (optional if name is provided)'),
  name: z.string().optional().describe('Contact name to search for (optional if id is provided)'),
});

export const findCompanySchema = z.object({
  id: z.string().optional().describe('UUID of the company (optional if name is provided)'),
  name: z.string().optional().describe('Company name to search for (optional if id is provided)'),
});

export const searchCrmSchema = z.object({
  query: z.string().describe('Search query'),
  entity_types: z.array(z.string()).optional().describe('Entity types to search (contacts, companies, deals, etc.)'),
  limit: z.number().optional().default(20).describe('Maximum number of results (default: 20)'),
});

export const auditContactTimelineSchema = z.object({
  contact_id: z.string().describe('UUID of the contact to audit'),
  limit: z.number().optional().default(20).describe('Number of activities to return (default: 20, max: 100)'),
  days_back: z.number().optional().default(30).describe('How many days back to look (default: 30)'),
  activity_types: z.array(z.string()).optional().describe('Optional filter by activity types (e.g., email, call, meeting)'),
  include_metadata: z.boolean().optional().default(false).describe('Include full metadata in results (default: false)'),
});

export const listContactsSchema = z.object({
  limit: z.number().optional().default(20).describe('Maximum number of contacts to return (default: 20, max: 100)'),
  orderBy: z.enum(['created_at', 'updated_at', 'name']).optional().default('created_at').describe('Field to order results by (default: created_at)'),
});

/**
 * Edit tool schemas
 */

export const editContactSchema = z.object({
  id: z.string().describe('UUID of the contact to edit'),
  updates: z.record(z.string(), z.any()).describe('Fields to update (name, email, phone, etc.)'),
});

export const editCompanySchema = z.object({
  id: z.string().describe('UUID of the company to edit'),
  updates: z.record(z.string(), z.any()).describe('Fields to update (name, industry, etc.)'),
});

export const createContactActivitySchema = z.object({
  contact_id: z.string().describe('UUID of the contact'),
  type_id: z.string().describe('UUID of communication type (email, call, meeting, note, etc.)'),
  title: z.string().describe('Activity title'),
  body: z.string().optional().describe('Activity description or details'),
  direction: z.enum(['inbound', 'outbound']).optional().describe('Communication direction (optional)'),
  status: z.string().optional().describe('Activity status (completed, pending, follow_up_needed, scheduled, cancelled)'),
  occurred_at: z.string().optional().describe('ISO 8601 timestamp when activity occurred (defaults to now)'),
  metadata: z.record(z.string(), z.any()).optional().describe('Additional metadata as JSON object'),
});

/**
 * Map of tool ID to Zod schema
 * Used by AI integration to build tool definitions
 */
export const toolZodSchemas = {
  find_contact: findContactSchema,
  find_company: findCompanySchema,
  search_crm: searchCrmSchema,
  audit_contact_timeline: auditContactTimelineSchema,
  list_contacts: listContactsSchema,
  edit_contact: editContactSchema,
  edit_company: editCompanySchema,
  create_contact_activity: createContactActivitySchema,
  create_artifact: createArtifactInputSchema,
} as const;

/**
 * Type-safe schema getter
 */
export function getToolSchema(toolId: string): z.ZodSchema | undefined {
  return toolZodSchemas[toolId as keyof typeof toolZodSchemas];
}
